// apps/frontend/src/components/profiles/ProfileSwitcher.tsx
// 👨‍👩‍👧‍👦 Composant ProfileSwitcher COMPLET CORRIGÉ
// ✅ Affichage correct selon profile_type
// ✅ Interface TypeScript avec avatar_url
// ✅ Optimisé mobile

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  Users,
  ChevronDown,
  Plus,
  Crown,
  Baby,
  LogOut,
  MessageSquare,
  ShoppingBag,
  Store,
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import Avatar from '../ui/Avatar';
import CreateProfileModal from './modals/CreateProfileModal';

// ============================================================================
// 🎯 INTERFACES TYPESCRIPT
// ============================================================================

export interface FamilyProfile {
  id: string;
  user_id: string;
  profile_name: string;
  profile_type: 'PARENT' | 'CHILD' | 'BUYER' | 'SELLER' | 'BUSINESS';
  parent_profile_id?: string | null;
  age?: number | null;
  birth_date?: string | null;
  gender?: string | null;
  avatar_url?: string | null; // ← AJOUT avatar_url
  size_preferences?: any;
  color_preferences?: any;
  brand_preferences?: any;
  style_preferences?: any;
  budget_limits?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProfileSwitcherProps {
  onProfileClick?: (profileId: string) => void;
  profiles: FamilyProfile[];
  activeProfile: FamilyProfile | null;
  hasProfiles: boolean;
  isLoading: boolean;
  onSwitchProfile?: (profileId: string) => void;
  onCreateProfile?: (profileData: any) => void;
  selectedProfileIds?: string[];
  onToggleSelection?: (profileId: string) => void;
}

// ============================================================================
// 🔧 FONCTION CALCUL D'ÂGE PRÉCIS
// ============================================================================

const calculateChildAge = (
  birthDate: string
): { ageValue: number; ageText: string; displayText: string } => {
  const today = new Date();
  const birth = new Date(birthDate);

  let totalMonths = (today.getFullYear() - birth.getFullYear()) * 12;
  totalMonths += today.getMonth() - birth.getMonth();

  if (today.getDate() < birth.getDate()) {
    totalMonths--;
  }

  if (totalMonths <= 36) {
    return {
      ageValue: totalMonths,
      ageText: `${totalMonths} mois`,
      displayText: `${totalMonths} mois • Profil enfant`,
    };
  } else {
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;

    let ageText = `${years} ${years === 1 ? 'an' : 'ans'}`;
    if (remainingMonths > 0) {
      ageText += ` et ${remainingMonths} mois`;
    }

    return {
      ageValue: years,
      ageText: ageText,
      displayText: `${ageText} • Profil enfant`,
    };
  }
};

// ============================================================================
// 🔧 FONCTION HELPER AFFICHAGE - CORRIGÉE
// ============================================================================

const getProfileAgeDisplay = (profile: FamilyProfile): string => {
  // ✅ CORRECTION : D'abord vérifier le TYPE de profil
  if (profile.profile_type === 'PARENT') {
    return 'Profil principal';
  }

  if (profile.profile_type === 'BUYER') {
    return 'Acheteur';
  }

  if (profile.profile_type === 'SELLER') {
    return 'Vendeur';
  }

  // Pour les profils CHILD, afficher l'âge
  if (profile.birth_date) {
    return calculateChildAge(profile.birth_date).ageText;
  }

  if (profile.age) {
    return `${profile.age} an${profile.age > 1 ? 's' : ''}`;
  }

  return 'Enfant';
};

// ============================================================================
// 🎨 PROFILE ROW — clic = navigation, bouton rond = sélection active
// ============================================================================

const ProfileRow: React.FC<{
  profile: FamilyProfile;
  isActive: boolean; // profil "actif" pour navigation/affichage
  isSelected: boolean; // coché pour les filtres recommandations
  onNavigate: () => void;
  onToggle: (e: React.MouseEvent) => void;
}> = ({ profile, isActive, isSelected, onNavigate, onToggle }) => {
  const isChild = profile.profile_type === 'CHILD';
  const ageLabel = getProfileAgeDisplay(profile);

  return (
    <div
      className={`flex items-center gap-2 p-2 sm:p-2.5 rounded-xl border transition-colors duration-150 cursor-pointer group ${
        isSelected
          ? 'border-brand-500 bg-[#fdf8f3]'
          : isActive
            ? 'border-brand-border bg-brand-offwhite'
            : 'border-brand-border hover:bg-brand-offwhite'
      }`}
      onClick={onNavigate}
    >
      {/* Avatar + badge */}
      <div className="relative flex-shrink-0">
        <Avatar
          src={profile.avatar_url ?? undefined}
          alt={profile.profile_name}
          name={profile.profile_name}
          size="md"
        />
        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border border-white bg-brand-500">
          {isChild ? (
            <Baby className="w-2 h-2 text-white" />
          ) : (
            <Crown className="w-2 h-2 text-white" />
          )}
        </div>
      </div>

      {/* Nom + sous-titre */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-brand-dark text-sm truncate">
          {profile.profile_name}
        </div>
        <div className="text-xs text-brand-warm truncate">{ageLabel}</div>
      </div>

      {/* Case à cocher ronde : inclure dans les filtres */}
      <button
        onClick={e => {
          e.stopPropagation();
          onToggle(e);
        }}
        title={isSelected ? 'Retirer des filtres' : 'Inclure dans les filtres'}
        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-150 ${
          isSelected
            ? 'border-brand-500 bg-brand-500'
            : 'border-brand-border hover:border-brand-500 bg-white'
        }`}
      >
        {isSelected && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

// ============================================================================
// 🎨 COMPOSANT PROFILE SWITCHER
// ============================================================================

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({
  onProfileClick: _onProfileClick,
  profiles,
  activeProfile,
  hasProfiles,
  isLoading,
  onSwitchProfile,
  onCreateProfile,
  selectedProfileIds = [],
  onToggleSelection,
}) => {
  const { t } = useTranslation('common');
  const { showError } = useToast();

  const childProfiles = profiles.filter(p => p.profile_type === 'CHILD');
  const parentProfile = profiles.find(p => p.profile_type === 'PARENT') || null;

  // Profil à afficher dans le bouton fermé = toujours le parent
  const displayProfile = parentProfile || activeProfile;
  const displayName =
    displayProfile?.profile_name || t('navigation.profile', 'Profil');

  // Profils enfants actuellement cochés
  const selectedChildren = childProfiles.filter(p =>
    selectedProfileIds.includes(p.id)
  );

  const navigate = useNavigate();
  const { logout } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Listener global : permet aux autres surfaces (HomeFamilyNudge,
  // FamilyLandingPage, onboarding...) d'ouvrir la modal de creation
  // de profil enfant via window.dispatchEvent('glowp:openCreateChild').
  // Centralise l'UX de creation au lieu de dupliquer la modal partout.
  useEffect(() => {
    const handler = () => setShowCreateForm(true);
    window.addEventListener('glowp:openCreateChild', handler);
    return () => window.removeEventListener('glowp:openCreateChild', handler);
  }, []);

  // 💬 Badge messages non lus — polling 30s
  const fetchUnread = useCallback(async () => {
    try {
      const token = localStorage.getItem('glowp_access_token');
      if (!token) return;
      const res = await fetch(
        `${API_BASE_URL}/api/v1/chat/conversations?limit=50`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCount(data.data?.summary?.unread ?? 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60_000); // 60s (was 30s)
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const handleNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  // ============================================================================
  // 🎯 HANDLERS
  // ============================================================================

  const handleSwitchProfile = async (profileId: string) => {
    try {
      setIsOpen(false);
      if (onSwitchProfile) {
        onSwitchProfile(profileId);
      }
    } catch (error) {
      console.error('❌ Erreur changement profil:', error);
    }
  };

  const handleCreateChildProfile = async (profileData: {
    name: string;
    birthDate: string;
    gender: 'MALE' | 'FEMALE' | 'UNISEX';
    avatar_url?: string;
    clothingSizes?: string[];
    shoeSizes?: string[];
    preferredBrands?: string[];
    favoriteColors?: string[];
    preferredStyles?: string[];
  }) => {
    try {
      setIsCreating(true);

      const today = new Date();
      const birth = new Date(profileData.birthDate);
      let totalMonths = (today.getFullYear() - birth.getFullYear()) * 12;
      totalMonths += today.getMonth() - birth.getMonth();
      if (today.getDate() < birth.getDate()) totalMonths--;

      const ageInYears = Math.floor(totalMonths / 12);

      const formattedData = {
        profile_name: profileData.name,
        profile_type: 'CHILD' as const,
        age: ageInYears,
        birth_date: profileData.birthDate,
        gender: profileData.gender,
        avatar_url: profileData.avatar_url,
        size_preferences: {
          clothes: profileData.clothingSizes || [],
          shoes: profileData.shoeSizes || [],
        },
        color_preferences: profileData.favoriteColors || [],
        brand_preferences: profileData.preferredBrands || [],
        style_preferences: profileData.preferredStyles || [],
      };

      if (onCreateProfile) {
        await onCreateProfile(formattedData);
      }

      setShowCreateForm(false);
      setIsOpen(false);
    } catch (error: any) {
      console.error('❌ Erreur création profil:', error);
      showError(`Erreur: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // ============================================================================
  // 🎨 RENDER
  // ============================================================================

  if (isLoading || (!hasProfiles && profiles.length === 0)) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-brand-light rounded-lg animate-pulse">
        <div className="w-8 h-8 bg-brand-border rounded-full"></div>
        <div className="w-16 h-4 bg-brand-border rounded"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 🎨 BOUTON HEADER */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-brand-dark hover:bg-brand-offwhite rounded-lg transition-all duration-200 hover:shadow-sm"
        style={{ backgroundColor: isOpen ? '#f3f4f6' : 'transparent' }}
      >
        {/* Avatar parent + avatars enfants cochés en stack */}
        <div className="flex items-center">
          {/* Avatar principal (parent) */}
          <div className="relative">
            <Avatar
              src={displayProfile?.avatar_url ?? undefined}
              alt={displayName}
              name={displayName}
              size="sm"
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center border border-white bg-brand-500">
              <Crown className="w-1.5 h-1.5 text-white" />
            </div>
          </div>

          {/* Avatars enfants cochés (max 2) */}
          {selectedChildren.slice(0, 2).map((child, i) => (
            <div
              key={child.id}
              className="relative -ml-2"
              style={{ zIndex: i + 1 }}
            >
              <Avatar
                src={child.avatar_url ?? undefined}
                alt={child.profile_name}
                name={child.profile_name}
                size="sm"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center border border-white bg-brand-500">
                <Baby className="w-1.5 h-1.5 text-white" />
              </div>
            </div>
          ))}

          {/* Badge "+n" si plus de 2 enfants cochés */}
          {selectedChildren.length > 2 && (
            <div className="-ml-2 w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white bg-brand-500">
              +{selectedChildren.length - 2}
            </div>
          )}
        </div>

        <span className="hidden sm:block font-medium max-w-24 truncate">
          {selectedChildren.length > 0
            ? `${displayName} +${selectedChildren.length}`
            : displayName}
        </span>

        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* 🎨 DROPDOWN MENU */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu RESPONSIVE - Mobile optimisé */}
          <div
            className="absolute mt-2 bg-white rounded-xl shadow-lg border border-brand-border z-20 overflow-hidden
                         right-0 
                         w-[calc(100vw-2rem)] sm:w-80 
                         max-w-[320px] sm:max-w-none"
          >
            {/* Header */}
            <div
              className="px-4 py-3 border-b border-brand-border"
              style={{ backgroundColor: '#f9fafb' }}
            >
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-500">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-dark text-sm sm:text-base">
                    Profils Famille
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-warm">
                    {profiles.length} profil{profiles.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Liste profils */}
            <div className="max-h-64 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-1.5">
                {/* Profil Parent */}
                {parentProfile && (
                  <ProfileRow
                    profile={parentProfile}
                    isActive={activeProfile?.id === parentProfile.id}
                    isSelected={selectedProfileIds.includes(parentProfile.id)}
                    onNavigate={() => handleNavigate('/account')}
                    onToggle={() =>
                      onToggleSelection
                        ? onToggleSelection(parentProfile.id)
                        : handleSwitchProfile(parentProfile.id)
                    }
                  />
                )}

                {/* Séparateur enfants */}
                {parentProfile && childProfiles.length > 0 && (
                  <div className="flex items-center gap-2 py-1 px-1">
                    <div className="flex-1 border-t border-brand-border" />
                    <span className="text-xs text-brand-muted font-medium">
                      enfants
                    </span>
                    <div className="flex-1 border-t border-brand-border" />
                  </div>
                )}

                {/* Profils Enfants */}
                {childProfiles.map(profile => (
                  <ProfileRow
                    key={profile.id}
                    profile={profile}
                    isActive={activeProfile?.id === profile.id}
                    isSelected={selectedProfileIds.includes(profile.id)}
                    onNavigate={() => handleNavigate(`/profile/${profile.id}`)}
                    onToggle={() =>
                      onToggleSelection
                        ? onToggleSelection(profile.id)
                        : handleSwitchProfile(profile.id)
                    }
                  />
                ))}

                {/* Légende multi-sélection */}
                {onToggleSelection && (
                  <p className="text-[10px] text-brand-muted text-center pt-1">
                    Cochez les profils pour filtrer les recommandations
                  </p>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div
              className="p-2 sm:p-3 border-t border-brand-border space-y-1"
              style={{ backgroundColor: '#f9fafb' }}
            >
              {/* Mes achats */}
              <button
                onClick={() => handleNavigate('/orders?tab=buying')}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-brand-dark hover:bg-white rounded-lg transition-colors"
              >
                <ShoppingBag className="w-4 h-4 text-brand-muted" />
                <span>Mes achats</span>
              </button>

              {/* Mes ventes */}
              <button
                onClick={() => handleNavigate('/seller/dashboard')}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-brand-dark hover:bg-white rounded-lg transition-colors"
              >
                <Store className="w-4 h-4 text-brand-muted" />
                <span>Mes ventes</span>
              </button>

              {/* Messages — avec badge non lus */}
              <button
                onClick={() => handleNavigate('/messages')}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-brand-dark hover:bg-white rounded-lg transition-colors"
              >
                <span className="relative">
                  <MessageSquare className="w-4 h-4 text-brand-muted" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
                <span>Messages</span>
                {unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                    {unreadCount}
                  </span>
                )}
              </button>

              <div className="border-t border-brand-border my-1" />

              {/* Créer profil enfant */}
              <button
                onClick={() => {
                  setShowCreateForm(true);
                  setIsOpen(false);
                }}
                disabled={isCreating}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all hover:opacity-90 disabled:opacity-50 text-sm"
                style={{
                  backgroundColor: 'var(--brand-500)',
                  color: '#ffffff',
                }}
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Création...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Créer un profil enfant</span>
                  </>
                )}
              </button>

              {/* Déconnexion */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>Se déconnecter</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal Création */}
      {showCreateForm && (
        <CreateProfileModal
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateChildProfile}
          isLoading={isCreating}
        />
      )}
    </div>
  );
};

export default ProfileSwitcher;
